import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Modal from './Modal';
import * as dateUtils from '../utils/dateUtils';

function UserDetail({ userInfo }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [timeRecords, setTimeRecords] = useState([]);
  const [formattedRecords, setFormattedRecords] = useState({});
  const [shifts, setShifts] = useState([]);
  const [formattedShifts, setFormattedShifts] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('attendance');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditRecordModalOpen, setIsEditRecordModalOpen] = useState(false);
  const [isEditShiftModalOpen, setIsEditShiftModalOpen] = useState(false);
  const [isEditGroupAttributesModalOpen, setIsEditGroupAttributesModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [editData, setEditData] = useState({
    name: '',
    role: '',
    clock_in: '',
    clock_out: '',
    note: '',
    modification_reason: '',
    break_time: '1'
  });
  const [editShiftData, setEditShiftData] = useState({
    start_time: '',
    end_time: '',
    is_workday: true
  });
  const [message, setMessage] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // グループ属性関連の状態
  const [groupAttributes, setGroupAttributes] = useState({
    client_company_id: '',
    job_assignment_id: '',
    employment_type_id: ''
  });
  const [clientCompanies, setClientCompanies] = useState([]);
  const [jobAssignments, setJobAssignments] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // 管理者権限チェック
    if (!userInfo || (userInfo.role !== 'admin' && userInfo.role !== 'super_admin')) {
      navigate('/time-clock');
      return;
    }

    fetchUserDetails();
    fetchTimeRecords();
    fetchShifts();
    
    // スーパー管理者の場合、グループ属性関連のデータを取得
    if (userInfo.role === 'super_admin') {
      fetchUserGroupAttributes();
      fetchClientCompanies();
      fetchEmploymentTypes();
    }
  }, [userId, selectedMonth, userInfo, navigate]);
  
  // クライアント企業が変更されたときに担当業務を取得
  useEffect(() => {
    if (groupAttributes.client_company_id) {
      fetchJobAssignments(groupAttributes.client_company_id);
    }
  }, [groupAttributes.client_company_id]);

  // ユーザー詳細情報を取得
  const fetchUserDetails = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('ユーザー情報の取得に失敗しました');
      }

      const data = await response.json();
      setUser(data);
      setEditData({
        name: data.name,
        role: data.role,
        clock_in: '',
        clock_out: '',
        note: '',
        modification_reason: '',
        break_time: '1'
      });
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // 勤怠記録を取得
  const fetchTimeRecords = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/time-records?month=${selectedMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('勤怠記録の取得に失敗しました');
      }

      const data = await response.json();
      console.log('取得した勤怠記録:', data); // デバッグ用
      setTimeRecords(data);
      
      // 日付ごとにデータを整形
      const formattedData = {};
      
      data.forEach(record => {
        if (!record || !record.recorded_at) return;
        
        // 日付文字列を生成（JST変換あり）
        const dateStr = dateUtils.formatDateToJST(record.recorded_at);
        
        if (!formattedData[dateStr]) {
          formattedData[dateStr] = {
            date: dateStr,
            records: []
          };
        }
        
        formattedData[dateStr].records.push(record);
      });
      
      // 各日付の出勤・退勤時間を特定
      Object.keys(formattedData).forEach(dateStr => {
        const dayRecords = formattedData[dateStr].records;
        let clockIn = null;
        let clockOut = null;
        let note = '';
        let absence_reason = null;
        let hasNormalRecords = false; // 通常の打刻記録があるかどうかのフラグ
        
        console.log(`日付 ${dateStr} の処理開始 - レコード数: ${dayRecords.length}`);
        
        // まず通常の打刻記録があるか確認
        dayRecords.forEach(record => {
          if (record.record_type === 'clock_in' || record.record_type === 'in' ||
              record.record_type === 'clock_out' || record.record_type === 'out') {
            hasNormalRecords = true;
          }
        });
        
        console.log(`日付 ${dateStr} - 通常の打刻記録あり: ${hasNormalRecords}`);
        
        dayRecords.forEach(record => {
          console.log(`日付 ${dateStr} - レコードID: ${record.id}, タイプ: ${record.record_type}, 記録時間: ${record.recorded_at}`);
          
          // 欠勤記録の場合（通常の打刻記録がない場合のみ処理）
          if (record.record_type === 'absence' && !hasNormalRecords) {
            absence_reason = record.absence_reason;
            note = record.note || '';
            console.log(`日付 ${dateStr} - 欠勤記録を処理: ${note}`);
            return;
          }
          
          // record_typeが 'clock_in' または 'in' の場合
          if (record.record_type === 'clock_in' || record.record_type === 'in') {
            // 日時をJST変換して使用
            const timeStr = dateUtils.formatTimeToJST(record.recorded_at);
            console.log('出勤記録ID:', record.id);
            console.log('出勤記録の元のUTC時間:', record.recorded_at);
            console.log('出勤記録の日本時間変換結果:', timeStr);
            
            if (!clockIn || timeStr < clockIn) {
              clockIn = timeStr;
              console.log(`日付 ${dateStr} - 出勤時間を更新: ${clockIn}`);
            }
          } 
          // record_typeが 'clock_out' または 'out' の場合
          else if (record.record_type === 'clock_out' || record.record_type === 'out') {
            // 日時をJST変換して使用
            const timeStr = dateUtils.formatTimeToJST(record.recorded_at);
            console.log('退勤記録ID:', record.id);
            console.log('退勤記録の元のUTC時間:', record.recorded_at);
            console.log('退勤記録の日本時間変換結果:', timeStr);
            
            if (!clockOut || timeStr > clockOut) {
              clockOut = timeStr;
              console.log(`日付 ${dateStr} - 退勤時間を更新: ${clockOut}`);
            }
          }
          
          if (record.note && (record.record_type === 'clock_in' || record.record_type === 'in' || 
                             record.record_type === 'clock_out' || record.record_type === 'out')) {
            note = record.note;
          }
        });
        
        formattedData[dateStr].clock_in = clockIn;
        formattedData[dateStr].clock_out = clockOut;
        formattedData[dateStr].note = note;
        formattedData[dateStr].absence_reason = absence_reason;
        formattedData[dateStr].id = dayRecords[0]?.id; // 最初のレコードIDを使用
      });
      
      console.log('整形した勤怠記録:', formattedData); // デバッグ用
      setFormattedRecords(formattedData);
    } catch (err) {
      console.error('Error fetching time records:', err);
      setMessage('勤怠記録の取得に失敗しました: ' + err.message);
    }
  };

  // シフト情報を取得
  const fetchShifts = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/shifts?month=${selectedMonth}`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('シフト情報の取得に失敗しました');
      }

      const data = await response.json();
      console.log('取得したシフト情報:', data); // デバッグ用
      setShifts(data);
      
      // 日付ごとにデータを整形
      const formatted = {};
      data.forEach(shift => {
        if (!shift || !shift.date) return;
        
        const dateObj = new Date(shift.date);
        const dateStr = dateObj.toISOString().split('T')[0];
        
        // 時間文字列を適切にフォーマット
        let startTime = '';
        if (shift.start_time) {
          // HH:MM:SS形式の時間文字列をDate型に変換してフォーマット
          const [hours, minutes] = shift.start_time.split(':');
          const timeDate = new Date();
          timeDate.setHours(hours, minutes, 0);
          startTime = timeDate.toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }

        let endTime = '';
        if (shift.end_time) {
          // HH:MM:SS形式の時間文字列をDate型に変換してフォーマット
          const [hours, minutes] = shift.end_time.split(':');
          const timeDate = new Date();
          timeDate.setHours(hours, minutes, 0);
          endTime = timeDate.toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        }
        
        formatted[dateStr] = {
          id: shift.id,
          date: dateStr,
          startTime: startTime,
          endTime: endTime,
          is_workday: shift.is_workday
        };
      });
      
      console.log('整形したシフト情報:', formatted); // デバッグ用
      setFormattedShifts(formatted);
    } catch (err) {
      console.error('Error fetching shifts:', err);
      setMessage('シフト情報の取得に失敗しました: ' + err.message);
    }
  };

  // ユーザーのグループ属性を取得
  const fetchUserGroupAttributes = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/group-attributes`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error('グループ属性の取得に失敗しました');
        return;
      }

      const data = await response.json();
      setGroupAttributes(data);
      
      // クライアント企業IDがあれば、その担当業務を取得
      if (data.client_company_id) {
        fetchJobAssignments(data.client_company_id);
      }
    } catch (error) {
      console.error('Error fetching group attributes:', error);
    }
  };
  
  // クライアント企業の取得
  const fetchClientCompanies = async () => {
    try {
      const response = await fetch('/api/admin/client-companies', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('クライアント企業の取得に失敗しました');
      }

      const data = await response.json();
      setClientCompanies(data);
    } catch (error) {
      console.error('Error fetching client companies:', error);
    }
  };
  
  // 担当業務の取得
  const fetchJobAssignments = async (clientCompanyId) => {
    try {
      const response = await fetch(`/api/admin/client-companies/${clientCompanyId}/job-assignments`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('担当業務の取得に失敗しました');
      }

      const data = await response.json();
      setJobAssignments(data);
    } catch (error) {
      console.error('Error fetching job assignments:', error);
    }
  };
  
  // 雇用区分の取得
  const fetchEmploymentTypes = async () => {
    try {
      const response = await fetch('/api/admin/employment-types', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('雇用区分の取得に失敗しました');
      }

      const data = await response.json();
      setEmploymentTypes(data);
    } catch (error) {
      console.error('Error fetching employment types:', error);
    }
  };
  
  // グループ属性の編集モーダルを開く
  const handleEditGroupAttributes = () => {
    if (userInfo.role !== 'super_admin') {
      alert('この操作を行う権限がありません');
      return;
    }
    setIsEditGroupAttributesModalOpen(true);
  };
  
  // グループ属性の変更を処理
  const handleGroupAttributeChange = (e) => {
    const { name, value } = e.target;
    setGroupAttributes(prev => ({
      ...prev,
      [name]: value
    }));
    
    // クライアント企業が変更された場合、担当業務をリセット
    if (name === 'client_company_id') {
      setGroupAttributes(prev => ({
        ...prev,
        job_assignment_id: ''
      }));
    }
  };
  
  // グループ属性の保存
  const handleSaveGroupAttributes = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/group-attributes`, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(groupAttributes)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'グループ属性の更新に失敗しました');
      }

      setIsEditGroupAttributesModalOpen(false);
      setMessage('グループ属性を更新しました');
    } catch (error) {
      console.error('Error saving group attributes:', error);
      setError(error.message);
    }
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleEditUser = () => {
    if (userInfo.role !== 'super_admin') {
      alert('この操作を行う権限がありません');
      return;
    }
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });

      if (!response.ok) {
        throw new Error('ユーザー情報の更新に失敗しました');
      }

      const updatedUser = await response.json();
      setUser(updatedUser);
      setIsEditModalOpen(false);
      alert('ユーザー情報を更新しました');
    } catch (err) {
      alert(err.message);
    }
  };

  // 勤怠記録の編集ボタンハンドラー
  const handleEditRecord = (dateStr) => {
    console.log('管理者ページ: 修正ボタンがクリックされました:', dateStr);
    const record = formattedRecords[dateStr] || { date: dateStr };
    
    console.log('修正対象の記録:', record);

    // 日付から対象の記録を特定
    const recordsForDate = timeRecords.filter(r => {
      if (!r || !r.recorded_at) return false;
      const recordDate = new Date(r.recorded_at);
      const recordDateStr = recordDate.toISOString().split('T')[0];
      return recordDateStr === dateStr;
    });
    
    console.log('対象日の記録:', recordsForDate);
    
    // レコードが存在しない場合は新規作成モードとして扱う
    const isNewRecord = recordsForDate.length === 0;
    console.log('新規作成モード:', isNewRecord);
    
    // 最初のレコードのIDを使用、または新規作成の場合は 'new'
    const recordId = isNewRecord ? 'new' : recordsForDate[0].id;
    console.log('選択されたレコードID:', recordId);

    setSelectedRecord({
      id: recordId,
      date: dateStr,
      ...record
    });

    // 時間をHTML time input形式（HH:MM）に変換
    let clockInTime = '';
    if (record.clock_in) {
      // 日本時間表記から時間と分を抽出
      const timeParts = record.clock_in.split(':');
      if (timeParts.length >= 2) {
        clockInTime = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}`;
      }
    }
    
    let clockOutTime = '';
    if (record.clock_out) {
      // 日本時間表記から時間と分を抽出
      const timeParts = record.clock_out.split(':');
      if (timeParts.length >= 2) {
        clockOutTime = `${timeParts[0].padStart(2, '0')}:${timeParts[1].padStart(2, '0')}`;
      }
    }

    setEditData({
      name: record.name || '',
      role: record.role || '',
      clock_in: clockInTime,
      clock_out: clockOutTime,
      note: record.note || '',
      modification_reason: '',
      break_time: '1'
    });
    
    console.log('編集モーダルを開きます');
    setIsEditRecordModalOpen(true);
  };

  // シフトの編集ボタンハンドラー
  const handleEditShift = (dateStr) => {
    const shift = formattedShifts[dateStr] || { startTime: '', endTime: '', is_workday: true };
    
    setSelectedShift({
      date: dateStr,
      ...shift
    });

    setEditShiftData({
      start_time: shift.startTime || '',
      end_time: shift.endTime || '',
      is_workday: shift.is_workday !== false
    });
    
    setIsEditShiftModalOpen(true);
  };

  // 勤怠記録の編集を保存
  const handleSaveRecordEdit = async () => {
    try {
      if (!selectedRecord) {
        setMessage('記録が選択されていません');
        return;
      }

      if (!editData.modification_reason) {
        setMessage('修正理由を入力してください');
        return;
      }

      // 出勤時間と退勤時間の検証
      let clockIn = editData.clock_in;
      let clockOut = editData.clock_out;

      // 時間形式の検証
      const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])(?::([0-5][0-9]))?$/;
      
      if (clockIn && !timeRegex.test(clockIn)) {
        setMessage('無効な出勤時間形式です。HH:MM または HH:MM:SS 形式で入力してください');
        return;
      }
      
      if (clockOut && !timeRegex.test(clockOut)) {
        setMessage('無効な退勤時間形式です。HH:MM または HH:MM:SS 形式で入力してください');
        return;
      }

      // 日本時間の時間文字列を取得
      console.log('使用するレコードID:', selectedRecord.id);
      
      // 日本時間の日付文字列
      const dateStr = selectedRecord.date;
      console.log('出勤時間（日本時間）:', clockIn);
      console.log('退勤時間（日本時間）:', clockOut);
      
      // フォーマット後の時間
      const formattedTimes = {
        clockIn: clockIn,
        clockOut: clockOut
      };
      console.log('フォーマット後の時間:', formattedTimes);

      // APIリクエスト - 管理者用エンドポイントを使用
      const url = `/api/admin/users/${userId}/time-records/${selectedRecord.id}`;
      console.log('リクエスト先URL:', url);
      
      const requestBody = {
        date: dateStr,
        clock_in: formattedTimes.clockIn,
        clock_out: formattedTimes.clockOut,
        note: editData.note,
        modification_reason: editData.modification_reason
      };
      console.log('リクエストボディ:', requestBody);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      console.log('レスポンスステータス:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.log('エラーレスポンス:', errorData);
        throw new Error(errorData.message || '勤怠記録の更新に失敗しました');
      }
      
      const result = await response.json();
      console.log('修正結果:', result);
      
      setMessage('勤怠記録を更新しました');
      setIsEditRecordModalOpen(false);
      fetchTimeRecords(); // 勤怠記録を再取得
    } catch (err) {
      console.log('修正保存エラー:', err);
      setMessage('勤怠記録の更新に失敗しました: ' + err.message);
    }
  };

  // シフト情報の更新を保存
  const handleSaveShiftEdit = async () => {
    if (!selectedShift) {
      setMessage('修正対象のシフトが見つかりません');
      return;
    }

    try {
      console.log('送信するシフトデータ:', {
        date: selectedShift.date,
        start_time: editShiftData.start_time,
        end_time: editShiftData.end_time,
        is_workday: editShiftData.is_workday
      }); // デバッグ用
      
      const response = await fetch(`/api/admin/users/${userId}/shifts/${selectedShift.id || 'new'}`, {
        method: selectedShift.id ? 'PUT' : 'POST',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date: selectedShift.date,
          start_time: editShiftData.start_time,
          end_time: editShiftData.end_time,
          is_workday: editShiftData.is_workday
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'シフト情報の更新に失敗しました');
      }

      setIsEditShiftModalOpen(false);
      setMessage('シフト情報を更新しました');
      fetchShifts(); // データを再取得
    } catch (err) {
      setMessage(err.message);
    }
  };

  // 勤怠記録の削除ボタンハンドラー
  const handleDeleteRecord = (record) => {
    console.log('削除ボタンがクリックされました:', record);
    setRecordToDelete(record);
    setIsDeleteConfirmModalOpen(true);
  };

  // 勤怠記録の削除確認ハンドラー
  const handleConfirmDelete = async () => {
    try {
      if (!recordToDelete || !recordToDelete.id) {
        setMessage('削除対象の記録が見つかりません');
        setIsDeleteConfirmModalOpen(false);
        return;
      }

      console.log('勤怠記録を削除します:', recordToDelete);

      // APIリクエスト
      const url = `/api/admin/users/${userId}/time-records/${recordToDelete.id}`;
      console.log('削除リクエスト先URL:', url);
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });
      
      console.log('削除レスポンスステータス:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.log('削除エラーレスポンス:', errorData);
        throw new Error(errorData.message || '勤怠記録の削除に失敗しました');
      }
      
      const result = await response.json();
      console.log('削除結果:', result);
      
      setMessage('勤怠記録を削除しました');
      setIsDeleteConfirmModalOpen(false);
      fetchTimeRecords(); // 勤怠記録を再取得
    } catch (err) {
      console.log('削除エラー:', err);
      setMessage('勤怠記録の削除に失敗しました: ' + err.message);
      setIsDeleteConfirmModalOpen(false);
    }
  };

  // 特定のフィールドを削除するハンドラー
  const handleDeleteField = async (recordId, field) => {
    try {
      if (!recordId) {
        setMessage('削除対象の記録が見つかりません');
        return;
      }

      console.log(`${field}を削除します:`, recordId);

      // APIリクエスト
      const url = `/api/admin/users/${userId}/time-records/${recordId}/field`;
      console.log('削除リクエスト先URL:', url);
      
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ field })
      });
      
      console.log('削除レスポンスステータス:', response.status);
      
      if (!response.ok) {
        // レスポンスのContent-Typeをチェック
        const contentType = response.headers.get('content-type');
        let errorMessage = '';
        
        if (contentType && contentType.includes('application/json')) {
          // JSONレスポンスの場合
          const errorData = await response.json();
          console.log('削除エラーレスポンス (JSON):', errorData);
          errorMessage = errorData.message || `${field}の削除に失敗しました`;
        } else {
          // テキストまたはその他のレスポンスの場合
          const errorText = await response.text();
          console.log('削除エラーレスポンス (テキスト):', errorText);
          errorMessage = `${field}の削除に失敗しました: サーバーエラー`;
        }
        
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('削除結果:', result);
      
      setMessage(`${field === 'clock_in' ? '出勤時間' : field === 'clock_out' ? '退勤時間' : field === 'break_time' ? '休憩時間' : '備考'}を削除しました`);
      fetchTimeRecords(); // 勤怠記録を再取得
    } catch (err) {
      console.log('削除エラー:', err);
      setMessage(`${field}の削除に失敗しました: ` + err.message);
    }
  };

  // 日時のフォーマット
  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    return dateUtils.formatDateToJST(dateTimeStr) + ' ' + dateUtils.formatTimeToJST(dateTimeStr);
  };

  // 日付のフォーマット
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return dateUtils.formatDateJP(dateStr);
  };

  // 月の日数を取得
  const getDaysInMonth = (yearMonth) => {
    return dateUtils.getDaysInMonth(yearMonth);
  };

  // 月の全日付を生成
  const generateDatesForMonth = (yearMonth) => {
    return dateUtils.generateDatesForMonth(yearMonth);
  };

  // 週末かどうかを判定
  const isWeekend = (date) => {
    return dateUtils.isWeekend(date);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">読み込み中...</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4">
        <button 
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
        >
          ← 戻る
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">ユーザー詳細</h1>
          {userInfo && userInfo.role === 'super_admin' && (
            <div>
              <button 
                onClick={handleEditUser}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded mr-2"
              >
                基本情報編集
              </button>
              <button 
                onClick={handleEditGroupAttributes}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
              >
                グループ属性編集
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-gray-600">社員ID:</p>
            <p className="font-semibold">{user.employee_id}</p>
          </div>
          <div>
            <p className="text-gray-600">名前:</p>
            <p className="font-semibold">{user.name}</p>
          </div>
          <div>
            <p className="text-gray-600">権限:</p>
            <p className="font-semibold">
              {user.role === 'super_admin' ? 'スーパー管理者' : 
               user.role === 'admin' ? '管理者' : 'ユーザー'}
            </p>
          </div>
          <div>
            <p className="text-gray-600">登録日:</p>
            <p className="font-semibold">{new Date(user.created_at).toLocaleDateString()}</p>
          </div>
          
          {userInfo && userInfo.role === 'super_admin' && (
            <>
              <div>
                <p className="text-gray-600">クライアント企業:</p>
                <p className="font-semibold">
                  {groupAttributes.client_company_id ? 
                    clientCompanies.find(c => c.id === parseInt(groupAttributes.client_company_id))?.name || '未設定' : 
                    '未設定'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">担当業務:</p>
                <p className="font-semibold">
                  {groupAttributes.job_assignment_id ? 
                    jobAssignments.find(j => j.id === parseInt(groupAttributes.job_assignment_id))?.name || '未設定' : 
                    '未設定'}
                </p>
              </div>
              <div>
                <p className="text-gray-600">雇用区分:</p>
                <p className="font-semibold">
                  {groupAttributes.employment_type_id ? 
                    employmentTypes.find(t => t.id === parseInt(groupAttributes.employment_type_id))?.name || '未設定' : 
                    '未設定'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {message}
        </div>
      )}

      <div className="mb-4">
        <div className="flex justify-between items-center">
          <div className="flex space-x-4">
            <button 
              onClick={() => setActiveTab('attendance')}
              className={`px-4 py-2 rounded ${activeTab === 'attendance' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
            >
              勤怠記録
            </button>
            <button 
              onClick={() => setActiveTab('shifts')}
              className={`px-4 py-2 rounded ${activeTab === 'shifts' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
            >
              シフト
            </button>
          </div>
          <div>
            <input 
              type="month" 
              value={selectedMonth}
              onChange={handleMonthChange}
              className="border rounded p-2"
            />
          </div>
        </div>
      </div>

      {activeTab === 'attendance' && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {/* デスクトップ表示 */}
          {!isMobile && (
            <table className="min-w-full bg-white border">
              <thead>
                <tr>
                  <th className="px-4 py-2">日付</th>
                  <th className="px-4 py-2">予定</th>
                  <th className="px-4 py-2">出勤</th>
                  <th className="px-4 py-2">退勤</th>
                  <th className="px-4 py-2">休憩時間</th>
                  <th className="px-4 py-2">備考</th>
                  <th className="px-4 py-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {generateDatesForMonth(selectedMonth).map((dateStr, index) => {
                  const [year, month, day] = dateStr.split('-').map(Number);
                  const date = new Date(year, month - 1, day);
                  const dayOfWeek = date.toLocaleDateString('ja-JP', { weekday: 'short' });
                  const shift = formattedShifts[dateStr] || { startTime: '', endTime: '' };
                  const record = formattedRecords[dateStr] || {};
                  
                  // 出勤・退勤レコードのIDを取得
                  const recordsForDate = timeRecords.filter(r => {
                    if (!r || !r.recorded_at) return false;
                    const recordDate = new Date(r.recorded_at);
                    const recordDateStr = recordDate.toISOString().split('T')[0];
                    return recordDateStr === dateStr;
                  });
                  
                  const inRecord = recordsForDate.find(r => r.record_type === 'in' || r.record_type === 'clock_in');
                  const outRecord = recordsForDate.find(r => r.record_type === 'out' || r.record_type === 'clock_out');
                  const inRecordId = inRecord ? inRecord.id : null;
                  const outRecordId = outRecord ? outRecord.id : null;
                  
                  // 休憩時間と備考のレコードIDを特定
                  // 休憩時間は退勤レコードに保存されることが多いため、まず退勤レコードを確認
                  const breakTimeRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);
                  const noteRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);

                  return (
                    <tr key={index} className={isWeekend(date) ? 'bg-gray-100' : ''}>
                      <td className="border px-4 py-2">
                        {`${date.getDate()}日(${dayOfWeek})`}
                      </td>
                      <td className="border px-4 py-2">
                        {shift.startTime && shift.endTime ? 
                          `${shift.startTime} - ${shift.endTime}` : 
                          '---'}
                      </td>
                      <td className="border px-4 py-2 relative">
                        {record.clock_in ? (
                          <div className="flex items-center">
                            <span>{record.clock_in}</span>
                            {inRecordId && (
                              <button 
                                onClick={() => handleDeleteField(inRecordId, 'clock_in')}
                                className="ml-2 text-red-500 hover:text-red-700"
                                title="出勤時間を削除"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ) : '---'}
                      </td>
                      <td className="border px-4 py-2 relative">
                        {record.clock_out ? (
                          <div className="flex items-center">
                            <span>{record.clock_out}</span>
                            {outRecordId && (
                              <button 
                                onClick={() => handleDeleteField(outRecordId, 'clock_out')}
                                className="ml-2 text-red-500 hover:text-red-700"
                                title="退勤時間を削除"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ) : '---'}
                      </td>
                      <td className="border px-4 py-2 relative">
                        {record.break_time ? (
                          <div className="flex items-center">
                            <span>{record.break_time}</span>
                            {breakTimeRecordId && (
                              <button 
                                onClick={() => handleDeleteField(breakTimeRecordId, 'break_time')}
                                className="ml-2 text-red-500 hover:text-red-700"
                                title="休憩時間を削除"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ) : '---'}
                      </td>
                      <td className="border px-4 py-2 relative">
                        {record.note ? (
                          <div className="flex items-center">
                            <span>{record.note}</span>
                            {noteRecordId && (
                              <button 
                                onClick={() => handleDeleteField(noteRecordId, 'note')}
                                className="ml-2 text-red-500 hover:text-red-700"
                                title="備考を削除"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ) : '---'}
                      </td>
                      <td className="border px-4 py-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditRecord(dateStr)}
                            className="bg-blue-500 text-white px-2 py-1 rounded text-sm"
                          >
                            修正
                          </button>
                          {(inRecordId || outRecordId) && (
                            <button
                              onClick={() => {
                                setRecordToDelete({
                                  id: inRecordId || outRecordId,
                                  date: dateStr
                                });
                                setIsDeleteConfirmModalOpen(true);
                              }}
                              className="bg-red-500 text-white px-2 py-1 rounded text-sm"
                            >
                              削除
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* モバイル表示 */}
          {isMobile && (
            <div className="space-y-4">
              {generateDatesForMonth(selectedMonth).map((dateStr, index) => {
                const [year, month, day] = dateStr.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                const dayOfWeek = date.toLocaleDateString('ja-JP', { weekday: 'short' });
                const shift = formattedShifts[dateStr] || { startTime: '', endTime: '' };
                const record = formattedRecords[dateStr] || {};
                
                // 出勤・退勤レコードのIDを取得
                const recordsForDate = timeRecords.filter(r => {
                  if (!r || !r.recorded_at) return false;
                  const recordDate = new Date(r.recorded_at);
                  const recordDateStr = recordDate.toISOString().split('T')[0];
                  return recordDateStr === dateStr;
                });
                
                const inRecord = recordsForDate.find(r => r.record_type === 'in' || r.record_type === 'clock_in');
                const outRecord = recordsForDate.find(r => r.record_type === 'out' || r.record_type === 'clock_out');
                const inRecordId = inRecord ? inRecord.id : null;
                const outRecordId = outRecord ? outRecord.id : null;
                
                // 休憩時間と備考のレコードIDを特定
                const breakTimeRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);
                const noteRecordId = outRecord ? outRecord.id : (inRecord ? inRecord.id : null);

                return (
                  <div 
                    key={index} 
                    className={`bg-white rounded-lg shadow-md p-4 ${isWeekend(date) ? 'bg-gray-50' : ''}`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-bold">
                        {`${date.getDate()}日(${dayOfWeek})`}
                      </h3>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditRecord(dateStr)}
                          className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                        >
                          修正
                        </button>
                        {(inRecordId || outRecordId) && (
                          <button
                            onClick={() => {
                              setRecordToDelete({
                                id: inRecordId || outRecordId,
                                date: dateStr
                              });
                              setIsDeleteConfirmModalOpen(true);
                            }}
                            className="bg-red-500 text-white px-2 py-1 rounded text-xs"
                          >
                            削除
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">予定: </span>
                        <span className="font-medium">
                          {shift.startTime && shift.endTime ? 
                            `${shift.startTime} - ${shift.endTime}` : 
                            '---'}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-600">出勤: </span>
                        <span className="font-medium ml-1">
                          {record.clock_in || '---'}
                        </span>
                        {record.clock_in && inRecordId && (
                          <button 
                            onClick={() => handleDeleteField(inRecordId, 'clock_in')}
                            className="ml-1 text-red-500 hover:text-red-700 text-xs"
                            title="出勤時間を削除"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-600">退勤: </span>
                        <span className="font-medium ml-1">
                          {record.clock_out || '---'}
                        </span>
                        {record.clock_out && outRecordId && (
                          <button 
                            onClick={() => handleDeleteField(outRecordId, 'clock_out')}
                            className="ml-1 text-red-500 hover:text-red-700 text-xs"
                            title="退勤時間を削除"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <div className="flex items-center">
                        <span className="text-gray-600">休憩: </span>
                        <span className="font-medium ml-1">
                          {record.break_time || '---'}
                        </span>
                        {record.break_time && breakTimeRecordId && (
                          <button 
                            onClick={() => handleDeleteField(breakTimeRecordId, 'break_time')}
                            className="ml-1 text-red-500 hover:text-red-700 text-xs"
                            title="休憩時間を削除"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </div>

                    {record.note && (
                      <div className="mt-2 text-sm flex items-center">
                        <span className="text-gray-600">備考: </span>
                        <span className="font-medium ml-1">
                          {record.note}
                        </span>
                        {noteRecordId && (
                          <button 
                            onClick={() => handleDeleteField(noteRecordId, 'note')}
                            className="ml-1 text-red-500 hover:text-red-700 text-xs"
                            title="備考を削除"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'shifts' && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {/* デスクトップ表示 */}
          {!isMobile && (
            <table className="min-w-full bg-white border">
              <thead>
                <tr>
                  <th className="px-4 py-2">日付</th>
                  <th className="px-4 py-2">開始時間</th>
                  <th className="px-4 py-2">終了時間</th>
                  <th className="px-4 py-2">休憩時間</th>
                  <th className="px-4 py-2">勤務日</th>
                  <th className="px-4 py-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {generateDatesForMonth(selectedMonth).map((dateStr, index) => {
                  const [year, month, day] = dateStr.split('-').map(Number);
                  const date = new Date(year, month - 1, day);
                  const dayOfWeek = date.toLocaleDateString('ja-JP', { weekday: 'short' });
                  const shift = formattedShifts[dateStr];

                  return (
                    <tr key={index} className={isWeekend(date) ? 'bg-gray-100' : ''}>
                      <td className="border px-4 py-2">
                        {`${date.getDate()}日(${dayOfWeek})`}
                      </td>
                      <td className="border px-4 py-2">
                        {shift?.startTime || '---'}
                      </td>
                      <td className="border px-4 py-2">
                        {shift?.endTime || '---'}
                      </td>
                      <td className="border px-4 py-2">
                        {shift?.is_workday !== false ? '1:00' : '---'}
                      </td>
                      <td className="border px-4 py-2">
                        {shift?.is_workday !== false ? '○' : '×'}
                      </td>
                      <td className="border px-4 py-2">
                        <button
                          onClick={() => handleEditShift(dateStr)}
                          className="bg-blue-500 text-white px-2 py-1 rounded text-sm"
                        >
                          修正
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* モバイル表示 */}
          {isMobile && (
            <div className="space-y-4 p-2">
              {generateDatesForMonth(selectedMonth).map((dateStr, index) => {
                const [year, month, day] = dateStr.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                const dayOfWeek = date.toLocaleDateString('ja-JP', { weekday: 'short' });
                const shift = formattedShifts[dateStr];
                const isWeekendDay = isWeekend(date);

                return (
                  <div 
                    key={index} 
                    className={`bg-white rounded-lg shadow-md p-4 ${isWeekendDay ? 'bg-gray-50' : ''}`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-bold">
                        {`${date.getDate()}日(${dayOfWeek})`}
                      </h3>
                      <button
                        onClick={() => handleEditShift(dateStr)}
                        className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                      >
                        修正
                      </button>
                    </div>

                    {shift?.is_workday !== false && (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">開始: </span>
                          <span className="font-medium">
                            {shift?.startTime || '---'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">終了: </span>
                          <span className="font-medium">
                            {shift?.endTime || '---'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">休憩: </span>
                          <span className="font-medium">1:00</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ユーザー編集モーダル */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">ユーザー情報編集</h2>
          <div className="space-y-4">
            <div>
              <label className="block mb-1">名前</label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({...editData, name: e.target.value})}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">権限</label>
              <select
                value={editData.role}
                onChange={(e) => setEditData({...editData, role: e.target.value})}
                className="w-full border rounded px-2 py-1"
              >
                <option value="user">一般ユーザー</option>
                <option value="admin">管理者</option>
                <option value="super_admin">スーパー管理者</option>
              </select>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveEdit}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* 勤怠記録編集モーダル */}
      <Modal isOpen={isEditRecordModalOpen} onClose={() => setIsEditRecordModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">勤怠記録修正</h2>
          <div className="space-y-4">
            <div>
              <label className="block mb-1">出勤時刻</label>
              <input
                type="time"
                value={editData.clock_in}
                onChange={(e) => setEditData({...editData, clock_in: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">退勤時刻</label>
              <input
                type="time"
                value={editData.clock_out}
                onChange={(e) => setEditData({...editData, clock_out: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">休憩時間（時間）</label>
              <select
                value={editData.break_time || "1"}
                onChange={(e) => setEditData({...editData, break_time: e.target.value})}
                className="border rounded px-2 py-1"
              >
                <option value="0.5">0:30</option>
                <option value="1">1:00</option>
                <option value="1.5">1:30</option>
                <option value="2">2:00</option>
              </select>
            </div>
            <div>
              <label className="block mb-1">修正理由 <span className="text-red-500">*</span></label>
              <textarea
                value={editData.modification_reason}
                onChange={(e) => setEditData({...editData, modification_reason: e.target.value})}
                className="w-full border rounded px-2 py-1"
                required
              />
            </div>
            <div>
              <label className="block mb-1">備考</label>
              <textarea
                value={editData.note}
                onChange={(e) => setEditData({...editData, note: e.target.value})}
                className="w-full border rounded px-2 py-1"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveRecordEdit}
                className="bg-blue-500 text-white px-4 py-2 rounded"
                disabled={!editData.modification_reason}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* シフト編集モーダル */}
      <Modal isOpen={isEditShiftModalOpen} onClose={() => setIsEditShiftModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">シフト修正</h2>
          <div className="space-y-4">
            <div>
              <label className="block mb-1">開始時間</label>
              <input
                type="time"
                value={editShiftData.start_time}
                onChange={(e) => setEditShiftData({...editShiftData, start_time: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div>
              <label className="block mb-1">終了時間</label>
              <input
                type="time"
                value={editShiftData.end_time}
                onChange={(e) => setEditShiftData({...editShiftData, end_time: e.target.value})}
                className="border rounded px-2 py-1"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_workday"
                checked={editShiftData.is_workday}
                onChange={(e) => setEditShiftData({...editShiftData, is_workday: e.target.checked})}
                className="mr-2"
              />
              <label htmlFor="is_workday">勤務日</label>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSaveShiftEdit}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* グループ属性編集モーダル */}
      <Modal 
        isOpen={isEditGroupAttributesModalOpen} 
        onClose={() => setIsEditGroupAttributesModalOpen(false)}
      >
        <h2 className="text-xl font-bold mb-4">グループ属性編集</h2>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2" htmlFor="client_company_id">
            クライアント企業
          </label>
          <select
            id="client_company_id"
            name="client_company_id"
            value={groupAttributes.client_company_id || ''}
            onChange={handleGroupAttributeChange}
            className="w-full p-2 border rounded"
          >
            <option value="">選択してください</option>
            {clientCompanies.map(company => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2" htmlFor="job_assignment_id">
            担当業務
          </label>
          <select
            id="job_assignment_id"
            name="job_assignment_id"
            value={groupAttributes.job_assignment_id || ''}
            onChange={handleGroupAttributeChange}
            className="w-full p-2 border rounded"
            disabled={!groupAttributes.client_company_id}
          >
            <option value="">選択してください</option>
            {jobAssignments.map(job => (
              <option key={job.id} value={job.id}>
                {job.name}
              </option>
            ))}
          </select>
          {!groupAttributes.client_company_id && (
            <p className="text-sm text-gray-500 mt-1">
              先にクライアント企業を選択してください
            </p>
          )}
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 mb-2" htmlFor="employment_type_id">
            雇用区分
          </label>
          <select
            id="employment_type_id"
            name="employment_type_id"
            value={groupAttributes.employment_type_id || ''}
            onChange={handleGroupAttributeChange}
            className="w-full p-2 border rounded"
          >
            <option value="">選択してください</option>
            {employmentTypes.map(type => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="flex justify-end">
          <button
            onClick={() => setIsEditGroupAttributesModalOpen(false)}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded mr-2"
          >
            キャンセル
          </button>
          <button
            onClick={handleSaveGroupAttributes}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            保存
          </button>
        </div>
      </Modal>

      {/* 削除確認モーダル */}
      <Modal isOpen={isDeleteConfirmModalOpen} onClose={() => setIsDeleteConfirmModalOpen(false)}>
        <div className="p-4">
          <h2 className="text-lg font-bold mb-4">勤怠記録削除の確認</h2>
          <p className="mb-4">以下の勤怠記録を削除してもよろしいですか？</p>
          
          {recordToDelete && (
            <div className="bg-gray-100 p-3 rounded mb-4">
              <p><span className="font-bold">日付:</span> {recordToDelete.date}</p>
              {recordToDelete.record_type === 'in' && (
                <p><span className="font-bold">出勤時間:</span> {dateUtils.formatTimeToJST(recordToDelete.recorded_at)}</p>
              )}
              {recordToDelete.record_type === 'out' && (
                <p><span className="font-bold">退勤時間:</span> {dateUtils.formatTimeToJST(recordToDelete.recorded_at)}</p>
              )}
              {recordToDelete.note && (
                <p><span className="font-bold">備考:</span> {recordToDelete.note}</p>
              )}
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsDeleteConfirmModalOpen(false)}
              className="bg-gray-300 text-gray-800 px-4 py-2 rounded"
            >
              キャンセル
            </button>
            <button
              onClick={handleConfirmDelete}
              className="bg-red-500 text-white px-4 py-2 rounded"
            >
              削除する
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default UserDetail; 